/**
 * Locales are discovered from src/locales/*.json: adding a language means adding one file.
 * en.json is the source of truth for keys (`pnpm i18n:check` compares the others with it).
 */
import type en from '../locales/en.json';

export type Messages = typeof en;

export const DEFAULT_LOCALE = 'en';

const RTL_LOCALES = new Set(['ar', 'fa', 'he', 'ur']);

const loaders = import.meta.glob<Messages>('../locales/*.json', { import: 'default' });

const loaderByLocale = new Map(
  Object.entries(loaders).map(([file, load]) => [file.replace(/^.*\/(.+)\.json$/, '$1'), load]),
);

/** All supported locales, the default one first. */
export const LOCALES: readonly string[] = [...loaderByLocale.keys()].sort((a, b) =>
  a === DEFAULT_LOCALE ? -1 : b === DEFAULT_LOCALE ? 1 : a.localeCompare(b),
);

export function isLocale(value: string | undefined): value is string {
  return value !== undefined && loaderByLocale.has(value);
}

/** `/hi/models` -> `hi`; undefined when the first path segment is not a locale. */
export function localeFromPath(pathname: string): string | undefined {
  const first = pathname.split('/')[1];
  return isLocale(first) ? first : undefined;
}

/** Path without the locale prefix: `/hi/models` -> `/models`, `/hi/` -> `/`. */
export function pathWithoutLocale(pathname: string): string {
  const locale = localeFromPath(pathname);
  if (locale === undefined) return pathname;
  const rest = pathname.slice(locale.length + 1);
  return rest === '' ? '/' : rest;
}

export function localizedPath(locale: string, path: string): string {
  return `/${locale}${path.startsWith('/') ? path : `/${path}`}`;
}

export function textDirection(locale: string): 'ltr' | 'rtl' {
  return RTL_LOCALES.has(locale) ? 'rtl' : 'ltr';
}

/** Language name in that language itself, e.g. `hi` -> `हिन्दी`. */
export function nativeLanguageName(locale: string): string {
  const name = new Intl.DisplayNames([locale], { type: 'language' }).of(locale) ?? locale;
  return name.charAt(0).toLocaleUpperCase(locale) + name.slice(1);
}

/** Best supported locale for the browser's preferred languages (`navigator.languages`). */
export function detectLocale(preferred: readonly string[]): string {
  for (const tag of preferred) {
    const base = tag.toLowerCase().split('-')[0];
    if (isLocale(base)) return base;
  }
  return DEFAULT_LOCALE;
}

export async function loadMessages(locale: string): Promise<Messages> {
  const load = loaderByLocale.get(locale);
  if (load === undefined) throw new Error(`Unsupported locale: ${locale}`);
  return load();
}
